<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-textarea
      data-testid="add-comment-textarea"
      v-model.trim="localValue.message"
      variant="outlined"
      :label="$t('modals.addNewComment.label')"
      :rules="[required()]"
      :placeholder="$t('modals.addNewComment.placeholder')"
      hide-details
    >
    </v-textarea>
    <div class="d-flex justify-space-between align-center w-100 mt-3">
      <div
        data-testid="notification-toggle"
        class="cursor-pointer text-no-wrap"
        @click="expand = !expand"
      >
        <v-icon icon="$triangle"></v-icon>
        <span class="ml-2 body2">{{ $t("general.notify") }}</span>
      </div>
      <div v-if="isGlobalAdmin">
        <v-switch
          data-testid="confidential-switch"
          v-model="confidential"
          :ripple="false"
          density="compact"
          inset
          flat
          color="primary"
          :label="$t('comments.confidential')"
        />
      </div>
    </div>
    <coro-comment-notification
      data-testid="comment-notification"
      v-if="expand"
      :settings="localValue"
      :is-in-modal="true"
      :confidential="confidential"
      @update-value="updateNotificationSettings($event)"
    />
  </v-form>
</template>

<script lang="ts">
import cloneDeep from "lodash/cloneDeep";
import CoroCommentNotification from "@/components/CoroCommentNotification.vue";
import { type CommentSettings, defaultCommentsSettings } from "@/_store/ticket-comments.module";
import { computed, defineComponent, ref, watch } from "vue";
import { required } from "@/_helpers/validators";
import type { VuetifyFormRef } from "@/types";
import { usePermissionsStore } from "@/_store/permissions.module.ts";

export default defineComponent({
  components: { CoroCommentNotification },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const localValue = ref({
      ...defaultCommentsSettings,
      ...props.config.item,
    });
    const form = ref<VuetifyFormRef>();

    const permissionsStore = usePermissionsStore();
    const confidential = ref(false);
    const valid = ref(false);
    const expand = ref(false);
    const isGlobalAdmin = computed(() => permissionsStore.isGlobalAdmin);

    watch(
      localValue,
      (value) => {
        emit("update:localValue", { ...value, confidential: confidential.value });
      },
      { deep: true, immediate: true }
    );
    watch(
      confidential,
      (value) => {
        emit("update:localValue", { ...localValue.value, confidential: value });
      },
      { deep: true, immediate: true }
    );

    const validate = async () => {
      const validationResult = await form.value?.validate();

      return validationResult?.valid;
    };

    const updateNotificationSettings = (event: CommentSettings) => {
      localValue.value = {
        ...localValue.value,
        ...cloneDeep(event),
      };
    };

    return {
      form,
      localValue,
      confidential,
      valid,
      expand,
      required,
      isGlobalAdmin,
      validate,
      updateNotificationSettings,
    };
  },
});
</script>

<style lang="scss" scoped></style>
